import { Game } from "../Game";

/**
 * How long a reading is gathered over before it is shown, in ms. The rate is a mean across the
 * window, so this is both the smoothing and the refresh: at 500ms the digits are readable and a
 * dropped frame still moves them. See the leaderboard's 5 Hz for the same reasoning.
 */
const WINDOW_MS = 500;

/**
 * The frame counter in the bottom-left corner: the mean frame rate over the last window, and the
 * single worst frame in it.
 *
 * Real DOM rather than Pixi, by design/UI.md's dividing question — it has no position in the sea,
 * it describes the session. It is also the one piece of chrome that must not itself cost a frame,
 * and two text nodes written twice a second cost nothing, whereas a Pixi `Text` re-rasterises a
 * glyph atlas every time its string changes.
 *
 * **The peak is the point.** A mean of 60 hides exactly the stutter somebody opens a frame counter
 * to find — one 90ms hitch inside a second of good frames still averages out near 60. The second
 * figure is that hitch.
 *
 * A **third** line appears while there is an arena to have a latency to: the round trip and its
 * jitter, pulled from whoever set {@link latency}. It belongs beside the frame rate rather than in
 * its own panel because the two answer the same question from opposite ends — a game that feels bad
 * is either not drawing or not hearing — and because everything about how a boat feels is judged at
 * a latency the developer's machine does not have. See design/Networking.md.
 *
 * Timed off `performance.now()` rather than the ticker's `deltaMS`, which Pixi clamps at `minFPS`
 * (100ms by default) — the clamp lands precisely on the frames worth seeing.
 */
export class FpsMeter {
    private readonly _root = document.createElement("div");
    private readonly _rate = document.createElement("span");
    private readonly _peak = document.createElement("span");
    private readonly _lag = document.createElement("span");

    /**
     * Where the round trip comes from, or null when nothing is connected. **Pulled, not pushed**:
     * the meter already wakes twice a second to publish a reading, so asking then costs one call
     * per window instead of a callback on every frame of a battle.
     *
     * Returning null is how "no reading yet" is said, and it is a real answer — Colyseus needs a
     * few input round trips before it has one.
     */
    public latency: (() => { rtt: number; jitter: number } | null) | null = null;
    /** Frames seen in the current window, and the longest gap between any two of them, in ms. */
    private _frames = 0;
    private _worstMs = 0;
    /** `performance.now()` at the start of the window, and at the previous frame. */
    private _windowStart = 0;
    private _previous = 0;

    constructor() {
        this._root.className = "fps";
        this._rate.className = "fps-rate";
        this._peak.className = "fps-peak";
        this._lag.className = "fps-lag";

        // It reports on the game rather than saying anything to a player, and it updates twice a
        // second — a screen reader announcing that forever is noise, not information.
        this._root.setAttribute("aria-hidden", "true");

        this._root.append(this._rate, this._peak, this._lag);
        this._reset(performance.now());
    }

    public mount(): void {
        document.body.appendChild(this._root);

        // The clock starts at mount and not at construction: anything between the two — a bundle
        // still parsing, the preloader — would otherwise land in the first reading as a hitch
        // that no frame actually took.
        this._reset(performance.now());
        Game.app.ticker.add(this._tick);
    }

    public destroy(): void {
        Game.app.ticker.remove(this._tick);
        this._root.remove();
    }

    /**
     * Counts the frame, and publishes a reading once the window is up. An arrow property so it is
     * the same function object on `remove` as it was on `add`.
     */
    private readonly _tick = (): void => {
        const now = performance.now();
        const frameMs = now - this._previous;

        this._previous = now;
        this._frames++;
        if (frameMs > this._worstMs) this._worstMs = frameMs;

        const elapsed = now - this._windowStart;
        if (elapsed < WINDOW_MS) return;

        this._rate.textContent = `${Math.round((this._frames * 1000) / elapsed)} FPS`;
        this._peak.textContent = `${Math.round(this._worstMs)} ms peak`;

        // Hidden rather than blanked, so the panel is two lines tall ashore and three at sea
        // instead of carrying an empty row around the menu.
        const latency = this.latency?.() ?? null;
        this._lag.hidden = latency === null;
        if (latency) this._lag.textContent = `${Math.round(latency.rtt)} ms rtt · ${Math.round(latency.jitter)} ms jitter`;

        this._reset(now);
    };

    private _reset(now: number): void {
        this._windowStart = now;
        this._previous = now;
        this._frames = 0;
        this._worstMs = 0;
    }
}
